/**
 * The one policy disclosure a page is allowed.
 *
 * DESIGN.md P7: "The editorial voice … is genuinely good — but it belongs in **one**
 * place per page, not appended to every section head and every card foot. Where it
 * explains policy rather than data, move it behind a single 'how this works'
 * disclosure."
 *
 * It is a component rather than a convention so the rule is checkable, and the check
 * is an **id** rather than a wrapper element or a data attribute: `Fold` already
 * takes an anchor id (`components/portal/common.ts` `Common`), a page linking to its
 * own policy fold wants one anyway, and a second `HowThisWorks` on one page is then
 * a duplicate id — which `rules.test.tsx` counts and which axe reports on its own.
 * Nothing here writes a class.
 *
 * ```tsx
 * <HowThisWorks summary="How a request becomes a charge">
 *   <Prose>You describe it; we price it off the rate card…</Prose>
 * </HowThisWorks>
 * ```
 */

import type { ReactNode } from 'react'

import { Fold } from '../portal/Disclosure.js'

/** The anchor, and what `rules.test.tsx` counts. One per page. */
export const HOW_THIS_WORKS_ID = 'how-this-works'

export type HowThisWorksProps = {
  /** The line on the closed disclosure. A question or a promise, never "More". */
  readonly summary: ReactNode
  readonly defaultOpen?: boolean
  readonly children: ReactNode
}

export function HowThisWorks({ summary, defaultOpen, children }: HowThisWorksProps) {
  return (
    <Fold id={HOW_THIS_WORKS_ID} summary={summary} defaultOpen={defaultOpen}>
      {children}
    </Fold>
  )
}
